import { useEffect, useState } from "react";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { DashboardSummaryRow } from "../../types/DashboardSummaryRow";
import { useBatteryEvents } from "./useBatteryEvents";
import { BatteryEventSummaryDto } from "./dtos/BatteryEventSummaryDto";
import { batteryEvents } from "../../data/batteryEvents.data";
import { DashboardSummary } from "../../components/DashboardSummary";
import { useVspPortalCookies } from "../../hooks/useVspPortalCookies";
import { Dashboard } from "../../enums/Dashboard";


const compareDashboardSummaryRows = ( a: DashboardSummaryRow, b: DashboardSummaryRow ) => {
    
    if ( a.displayOrder && b.displayOrder &&a.displayOrder < b.displayOrder ){
       
        return -1;

    }
    
    if (a.displayOrder && b.displayOrder && a.displayOrder > b.displayOrder ){
      
        return 1;

    }

    return 0;

}


const getRows = ( batteryEventsSummary: BatteryEventSummaryDto[] ): DashboardSummaryRow[] => {

    const rows: DashboardSummaryRow[] = [];

    for( const batteryEventSummary of batteryEventsSummary ) {
        rows.push( { count: batteryEventSummary.doorCount, icon: <QuestionMarkIcon />, text: '', item: batteryEventSummary.event  } );

    }

    // missing events
    const events = rows.map( r => r.item );
    for( const batteryEvent of batteryEvents ) {

        if( !events.includes( batteryEvent.event )) {

            rows.push( { count: 0, icon: <QuestionMarkIcon />, text: '', item: batteryEvent.event  } );

        }

    }

   
    // formatting
    const formattedRows: DashboardSummaryRow[ ] = [ ];

    for( const row of rows ) {

        const batteryEvent = batteryEvents.find( be => be.event === row.item );

        if( !batteryEvent ) {
            
            throw Error( `Battery event ${row.item} not found` );

        }

        formattedRows.push ({ 
            displayOrder: batteryEvent.displayOrder,
            headerText: batteryEvent.displayedHeader,
            count: row.count,
            icon: batteryEvent.icon,
            text: batteryEvent.displayedText,
            item: row.item 
        });

    }

     // sorting
     formattedRows.sort( compareDashboardSummaryRows );


    return formattedRows;

};


type BatteryEventsSummaryProps = {

    manualRefreshCount: number;
    onEventsSelected( events: string[ ] ): void;
    refreshCount: number;
    setBatteryEventsSummaryLoaded( _: boolean) : void;
    villageCode: string;

};


export const BatteryEventsSummary: React.FC<BatteryEventsSummaryProps> = ( props: BatteryEventsSummaryProps ) => {

    const { manualRefreshCount, onEventsSelected, refreshCount, setBatteryEventsSummaryLoaded, villageCode } = props;

    const [ selectedEvents, setSelectedEvents ] = useState< string[] >( [] );
    const [ rows, setRows ] = useState<DashboardSummaryRow[ ]>( [ ] );
    const [ isLoading, setIsLoading ] = useState<boolean>( false );

    const { getBatteryEventsSummary } = useBatteryEvents( );

    const { getEventsCookies, getVillageCookies } = useVspPortalCookies( );


    useEffect( ( ) => {

        if( !villageCode || manualRefreshCount === 0 ) {

            return;

        }

        const fetchBatteryEventsSummary = async ( ) => {

            setIsLoading( true );
            const eventsSummary : BatteryEventSummaryDto[ ] | null = await getBatteryEventsSummary( villageCode );
            setIsLoading( false );
            
            processBatteryEventsSummary( eventsSummary );

        };
        
        fetchBatteryEventsSummary( );

    }, [ manualRefreshCount ]);


    useEffect( ( ) => {

        if( !villageCode ) {

            return;

        }
        
        const fetchBatteryEventsSummary = async ( ) => {

            if( refreshCount === 0 ) {
                
                setIsLoading( true );
            }

            const eventsSummary : BatteryEventSummaryDto[ ] | null = await getBatteryEventsSummary( villageCode );
            
            if( refreshCount === 0 ) {
            
                setIsLoading( false );

            }

            processBatteryEventsSummary( eventsSummary );

        };
        
        fetchBatteryEventsSummary( );

    }, [ villageCode, refreshCount ]);


    const processBatteryEventsSummary = ( eventsSummary : BatteryEventSummaryDto[ ] | null ) => {

        if( eventsSummary && eventsSummary.length > 0 ) {
                
            const rows: DashboardSummaryRow[] = getRows( eventsSummary ); 
            setRows( rows );

            const villageCookie = getVillageCookies( Dashboard.BatteryLevel );
            const batteryLevelEvents = getEventsCookies( Dashboard.BatteryLevel );

            if( villageCookie && villageCookie === villageCode && batteryLevelEvents ) {

                setSelectedEvents( batteryLevelEvents );
                onEventsSelected( batteryLevelEvents );
    
            } else {

                setSelectedEvents( [] );

            }


        } else {

            setRows( [] );
            setSelectedEvents( [] );

        }

        setBatteryEventsSummaryLoaded( true );

    };


    return (

        <DashboardSummary header="Category" subHeader="Voltage (V)" footer="No Of Doors" isLoading={isLoading} refreshCount={refreshCount} rows={rows} checkedItems={ selectedEvents } disabledItems={ [ ] } onItemsSelected={onEventsSelected} />

    );

};